import React, { useState, useEffect } from "react";
import {
  Bars3Icon,
  XMarkIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import {
  MenuItem,
  MenuTab,
  SingleBurger,
  SignatureBurger,
  ShawarmaItem,
  ComboItem,
  SideItem,
  MenuTabType,
} from "../../types";

const HomePage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [activeMenuTab, setActiveMenuTab] = useState<MenuTabType>("burgers");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuItems: MenuItem[] = [
    {
      name: "Classic Burger",
      description:
        "Juicy beef patty with fresh lettuce, tomato, and our special sauce",
      price: "₦2,500",
      image:
        "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
    },
    {
      name: "Cheese Burger",
      description: "Classic burger topped with melted cheddar cheese",
      price: "₦2,800",
      image:
        "https://images.unsplash.com/photo-1586190848861-99aa4a171e90?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
    },
    {
      name: "Bacon Burger",
      description: "Premium beef with crispy bacon and caramelized onions",
      price: "₦3,200",
      image:
        "https://images.unsplash.com/photo-1553979459-d2229ba7433b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
    },
    {
      name: "Veggie Burger",
      description: "Plant-based patty with fresh vegetables and vegan cheese",
      price: "₦2,300",
      image:
        "https://images.unsplash.com/photo-1520072959219-c595dc870360?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
    },
  ];

  const singleBurgers: SingleBurger[] = [
    {
      name: "Crispy Chicken Burger",
      price: "₦3,950",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Single Chicken Cheeseburger",
      price: "₦4,800",
      image:
        "https://images.unsplash.com/photo-1586190848861-99aa4a171e90?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Spicy Beef Burger",
      price: "₦3,950",
      image:
        "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Single Beef Cheeseburger",
      price: "₦4,800",
      image:
        "https://images.unsplash.com/photo-1553979459-d2229ba7433b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Yankee Burger",
      price: "₦4,800",
      image:
        "https://images.unsplash.com/photo-1586190848861-99aa4a171e90?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
  ];

  const signatureBurgers: SignatureBurger[] = [
    {
      name: "Masterpiece Burger",
      price: "₦8,500",
      description: "Beef Patty | Suya Chunks | Tomato Jam | Cheese | Creamy",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Suya Beef Burger",
      price: "₦4,900",
      description: "Suya Beef Chunks | Suya Spice | Spicy Cream",
      image:
        "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Royal Burger",
      price: "₦7,800",
      description: "2x Beef or Chicken Patties | Bacons | Cheese | Creamy",
      image:
        "https://images.unsplash.com/photo-1553979459-d2229ba7433b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Mayor's Coconut Burger",
      price: "₦6,900",
      description:
        "Chicken Patties & Beef Patties | Coconut Bun | Cheese | Creamy",
      image:
        "https://images.unsplash.com/photo-1586190848861-99aa4a171e90?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
  ];

  const doubleBurgers: SingleBurger[] = [
    {
      name: "Double-Decker Chicken Cheeseburger",
      price: "₦6,200",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Double-Decker Beef Cheeseburger",
      price: "₦6,800",
      image:
        "https://images.unsplash.com/photo-1553979459-d2229ba7433b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
  ];

  const shawarmaItems: ShawarmaItem[] = [
    {
      name: "Chicken Shawarma (1ss)",
      price: "₦3,600",
      image:
        "https://images.unsplash.com/photo-1626645738196-c2a7c87a8f58?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Chicken Shawarma (2ss)",
      price: "₦4,000",
      image:
        "https://images.unsplash.com/photo-1626645738196-c2a7c87a8f58?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "BBQ Beef Shawarma (1ss)",
      price: "₦3,500",
      image:
        "https://images.unsplash.com/photo-1626645738196-c2a7c87a8f58?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "BBQ Beef Shawarma (2ss)",
      price: "₦4,000",
      image:
        "https://images.unsplash.com/photo-1626645738196-c2a7c87a8f58?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Mixed Shawarma",
      price: "₦4,000",
      image:
        "https://images.unsplash.com/photo-1626645738196-c2a7c87a8f58?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Suya Shawarma (Beef)",
      price: "₦4,900",
      image:
        "https://images.unsplash.com/photo-1626645738196-c2a7c87a8f58?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
  ];

  const combos: ComboItem[] = [
    {
      name: "Beef Cheeseburger & Smoothies",
      price: "₦7,800",
      description:
        "Delicious beef cheeseburger paired with refreshing smoothies",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Crispy Chicken Burger & Smoothies",
      price: "₦6,950",
      description: "Crispy chicken burger with your choice of smoothies",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Suya Burger/Shawarma & Smoothies",
      price: "₦7,900",
      description: "Spicy suya burger or shawarma with smoothies",
      image:
        "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Double Beef Cheeseburger & Smoothies",
      price: "₦9,800",
      description: "Double the beef, double the cheese with smoothies",
      image:
        "https://images.unsplash.com/photo-1553979459-d2229ba7433b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Funny Nuggets Box",
      price: "₦10,150",
      description:
        "Chicken Cheeseburger | 5 Chicken Nuggets | Fries | Soft drink",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Deluxe Burger Meal",
      price: "₦13,800",
      description:
        "Crispy Chicken Burger | Fries | 2 Crispy Chicken | Shawarma | Sugarcane Juice",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Weekend Burger Fest",
      price: "₦28,999",
      description: "4 Double Beef Cheeseburger & 4 Drinks",
      image:
        "https://images.unsplash.com/photo-1553979459-d2229ba7433b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Family Burger Meal",
      price: "₦51,200",
      description: "4 Crispy Chicken Burger | 4 Fries | 8 Chicken",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
  ];

  const sidesAndDrinks: SideItem[] = [
    {
      name: "Bacon",
      price: "₦800",
      image:
        "https://images.unsplash.com/photo-1528607929212-2636ec44253e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Cheese slice",
      price: "₦800",
      image:
        "https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Coke, Fanta or Sprite",
      price: "₦600",
      image:
        "https://images.unsplash.com/photo-1629203851122-3726ecdf080e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Supa Kommando",
      price: "₦600",
      image:
        "https://images.unsplash.com/photo-1629203851122-3726ecdf080e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Smoothies",
      price: "₦3,000",
      image:
        "https://images.unsplash.com/photo-1505252585461-04db1eb84625?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Sugarcane Juice",
      price: "₦1,500",
      image:
        "https://images.unsplash.com/photo-1629203851122-3726ecdf080e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Sausage",
      price: "₦500",
      image:
        "https://images.unsplash.com/photo-1528607929212-2636ec44253e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Fries/Chips",
      price: "₦1,750",
      image:
        "https://images.unsplash.com/photo-1573080496219-bb080dd4f877?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Crispy Chicken",
      price: "₦1,500",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Spicy Chicken",
      price: "₦1,500",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
    {
      name: "Wings",
      price: "₦1,200",
      image:
        "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    },
  ];

  const menuTabs: MenuTab[] = [
    { id: "burgers", name: "Burgers", icon: "🍔" },
    { id: "signature", name: "Signature", icon: "👑" },
    { id: "shawarma", name: "Shawarma", icon: "🥙" },
    { id: "combos", name: "Combos", icon: "📦" },
    { id: "sides", name: "Sides & Drinks", icon: "🥤" },
  ];

  const renderMenuContent = (): React.JSX.Element => {
    switch (activeMenuTab) {
      case "burgers":
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-display font-bold text-burger-red mb-4">
                Single Patty Burgers
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {singleBurgers.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold text-gray-900">
                          {item.name}
                        </h4>
                        <span className="text-burger-red font-bold">
                          {item.price}
                        </span>
                      </div>
                      <button className="btn-primary w-full mt-3">
                        Add to Cart
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-display font-bold text-burger-red mb-4">
                Double Patty Burgers
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {doubleBurgers.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold text-gray-900">
                          {item.name}
                        </h4>
                        <span className="text-burger-red font-bold">
                          {item.price}
                        </span>
                      </div>
                      <button className="btn-primary w-full mt-3">
                        Add to Cart
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case "signature":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {signatureBurgers.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-bold text-lg text-gray-900">
                      {item.name}
                    </h4>
                    <span className="text-burger-red font-bold text-xl">
                      {item.price}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    {item.description}
                  </p>
                  <button className="btn-primary w-full">Add to Cart</button>
                </div>
              </div>
            ))}
          </div>
        );
      case "shawarma":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shawarmaItems.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-900">{item.name}</h4>
                    <span className="text-burger-red font-bold">
                      {item.price}
                    </span>
                  </div>
                  <button className="btn-primary w-full mt-3">
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      case "combos":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {combos.map((item, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-burger-red to-burger-orange rounded-xl text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-bold text-lg">{item.name}</h4>
                    <span className="font-bold text-xl">{item.price}</span>
                  </div>
                  <p className="text-burger-cream text-sm mb-4">
                    {item.description}
                  </p>
                  <button className="bg-white text-burger-red hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg transition-all duration-300 w-full">
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      case "sides":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sidesAndDrinks.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-900">{item.name}</h4>
                    <span className="text-burger-red font-bold">
                      {item.price}
                    </span>
                  </div>
                  <button className="btn-primary w-full mt-3">
                    Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      default:
        return <div></div>;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          isScrolled ? "bg-white shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="container-custom px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1
                className={`text-2xl font-display font-bold ${
                  isScrolled ? "text-burger-red" : "text-white"
                }`}
              >
                The Burger Place
              </h1>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="#home"
                className={`transition-colors ${
                  isScrolled
                    ? "text-gray-700 hover:text-burger-red"
                    : "text-white hover:text-burger-cream"
                }`}
              >
                Home
              </a>
              <a
                href="#menu"
                className={`transition-colors ${
                  isScrolled
                    ? "text-gray-700 hover:text-burger-red"
                    : "text-white hover:text-burger-cream"
                }`}
              >
                Menu
              </a>
              <a
                href="#about"
                className={`transition-colors ${
                  isScrolled
                    ? "text-gray-700 hover:text-burger-red"
                    : "text-white hover:text-burger-cream"
                }`}
              >
                About
              </a>
              <a
                href="#contact"
                className={`transition-colors ${
                  isScrolled
                    ? "text-gray-700 hover:text-burger-red"
                    : "text-white hover:text-burger-cream"
                }`}
              >
                Contact
              </a>
              <button className="btn-primary">Order Now</button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <XMarkIcon
                  className={`h-6 w-6 ${
                    isScrolled ? "text-gray-700" : "text-white"
                  }`}
                />
              ) : (
                <Bars3Icon
                  className={`h-6 w-6 ${
                    isScrolled ? "text-gray-700" : "text-white"
                  }`}
                />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden bg-white shadow-lg rounded-lg mt-2 py-4">
              <div className="flex flex-col space-y-4 px-4">
                <a
                  href="#home"
                  className="text-gray-700 hover:text-burger-red transition-colors"
                >
                  Home
                </a>
                <a
                  href="#menu"
                  className="text-gray-700 hover:text-burger-red transition-colors"
                >
                  Menu
                </a>
                <a
                  href="#about"
                  className="text-gray-700 hover:text-burger-red transition-colors"
                >
                  About
                </a>
                <a
                  href="#contact"
                  className="text-gray-700 hover:text-burger-red transition-colors"
                >
                  Contact
                </a>
                <button className="btn-primary w-full">Order Now</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-burger-red via-burger-orange to-burger-yellow"
      >
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-6">
            The Best Burgers
            <span className="block text-burger-cream">in Lagos</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto">
            Experience the juiciest, most flavorful burgers made with premium
            ingredients and love
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-secondary text-lg px-8 py-4">
              Order Now
            </button>
            <button className="bg-white text-burger-red hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 text-lg">
              View Menu
            </button>
          </div>
        </div>

        {/* Floating burger image */}
        <div className="absolute bottom-10 right-10 hidden lg:block">
          <img
            src="https://images.unsplash.com/photo-1568901346375-23c9450c58cd?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
            alt="Delicious Burger"
            className="w-64 h-64 object-cover rounded-full shadow-2xl"
          />
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
              Our Famous Menu
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover our signature burgers crafted with the finest ingredients
              and authentic recipes
            </p>
          </div>

          {/* Menu Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {menuTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveMenuTab(tab.id as MenuTabType)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  activeMenuTab === tab.id
                    ? "bg-burger-red text-white shadow-lg"
                    : "bg-white text-gray-700 hover:bg-burger-red hover:text-white shadow-md"
                }`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>

          {/* Menu Content */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {renderMenuContent()}
          </div>

          <div className="text-center mt-12">
            <button className="btn-secondary text-lg px-8 py-4">
              Download Full Menu
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
                The Story Behind
                <span className="block text-burger-red">The Burger Place</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Founded in 2020, The Burger Place started as a small food truck
                in Yaba, Lagos. Our passion for creating the perfect burger
                drove us to source only the finest ingredients and develop
                unique recipes that keep our customers coming back for more.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Today, we're proud to serve the most delicious burgers in Lagos,
                with locations across the city and a commitment to quality that
                never waivers.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-burger-red mb-2">
                    10K+
                  </div>
                  <div className="text-gray-600">Happy Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-burger-red mb-2">
                    5
                  </div>
                  <div className="text-gray-600">Locations</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Our Kitchen"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-burger-red text-white p-6 rounded-lg">
                <div className="text-2xl font-bold">Since 2020</div>
                <div className="text-sm">Serving Lagos</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-gray-900 text-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Visit Us Today
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Come experience the best burgers in Lagos at any of our locations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-800 p-8 rounded-2xl">
              <div className="flex items-center mb-4">
                <MapPinIcon className="h-8 w-8 text-burger-red mr-3" />
                <h3 className="text-xl font-semibold">Yaba Location</h3>
              </div>
              <p className="text-gray-300 mb-4">
                123 Herbert Macaulay Way, Yaba, Lagos
              </p>
              <div className="flex items-center text-gray-300">
                <ClockIcon className="h-5 w-5 mr-2" />
                <span>Open Daily 10AM - 10PM</span>
              </div>
            </div>

            <div className="bg-gray-800 p-8 rounded-2xl">
              <div className="flex items-center mb-4">
                <PhoneIcon className="h-8 w-8 text-burger-red mr-3" />
                <h3 className="text-xl font-semibold">Contact Us</h3>
              </div>
              <p className="text-gray-300 mb-4">
                Call us for orders and inquiries
              </p>
              <div className="text-burger-red font-semibold text-lg">
                +234 801 234 5678
              </div>
            </div>

            <div className="bg-gray-800 p-8 rounded-2xl">
              <div className="flex items-center mb-4">
                <div className="h-8 w-8 bg-burger-red rounded-full mr-3 flex items-center justify-center">
                  <span className="text-white font-bold">IG</span>
                </div>
                <h3 className="text-xl font-semibold">Follow Us</h3>
              </div>
              <p className="text-gray-300 mb-4">
                Stay updated with our latest offerings
              </p>
              <div className="space-y-2">
                <a
                  href="https://www.instagram.com/theburgerplace.yaba/"
                  className="block text-burger-red hover:text-burger-orange transition-colors"
                >
                  @theburgerplace.yaba
                </a>
                <a
                  href="https://www.instagram.com/theburgerplace.ng/"
                  className="block text-burger-red hover:text-burger-orange transition-colors"
                >
                  @theburgerplace.ng
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container-custom text-center">
          <p className="text-gray-400">
            © 2024 The Burger Place. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
